import { FC, HTMLAttributes } from 'react';
import styled, { useTheme } from 'styled-components';
import { defaultTheme } from '../../context';
import { StyledTooltipIcon } from '../TooltipIcon';

/** Original (old) self-reg theme version */
const InputContainerOriginal = styled.div`
  position: relative;
  transition: all 0.2s ease-in-out;
  flex-grow: 1;
  height: 48px;

  > input,
  > select,
  > textarea {
    line-height: 1.2;
    box-sizing: border-box;
    border: solid 1px #ccc;
    height: 100%;
    width: 100%;
    font-size: 15px;
    outline: 0;

    @media (min-width: 1000px) {
      border-radius: 6px;
    }

    :disabled {
      background: #e6e6e6;
      cursor: not-allowed;
    }

    :not(:disabled) {
      background: white;

      &:focus,
      &:hover {
        border-color: ${props => props.theme.colors.primaryAccent};
      }
    }

    &.-error {
      border-color: #be1e2d;
    }
  }

  > input {
    padding: 8px 10px 0 10px;
  }

  > textarea {
    transition: 0.3s;
    border-bottom-right-radius: 0px;
    padding: 16px 10px 8px 12px;
    min-height: 47px;
    resize: vertical;
  }

  > input[type='number']::-webkit-outer-spin-button,
  > input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  > input[type='number'] {
    -moz-appearance: textfield;
  }

  > input[type='date'] {
    padding: 8px 10px 0 7px;
  }

  > select {
    cursor: pointer;
    padding: 8px 20px 0 7px;

    /* hide default select arrow */
    appearance: none;
    ::-ms-expand {
      display: none;
    }
  }

  > div {
    padding-left: 10px;

    > label {
      z-index: 2;
      cursor: text;
      color: lightgray;
      position: absolute;
      transform-origin: top left;
      transform: translate(0, 14px) scale(1);
      transition: all 0.1s ease-in-out;
    }
  }

  > ${StyledTooltipIcon} {
    font-size: 1rem;
    position: absolute;
    top: 40%;
    right: 11px;
    cursor: help;
    color: ${props => props.theme.colors.primaryAccent};
  }

  > i.select-indicator {
    font-size: 60%;
    pointer-events: none;
    position: absolute;
    top: 45%;
    right: 14px;
  }

  &.-required {
    > div > label:after {
      content: '*';
      color: red;
      margin-left: 0.5ex;
    }
  }

  &.-active {
    > div > label {
      color: gray;
      transform: translate(0, 4px) scale(0.7);
    }
  }
`;

InputContainerOriginal.defaultProps = {
  theme: defaultTheme as any,
};

/** 'Gogurt' theme version */
const InputContainerGogurt = styled.div`
  position: relative;
  flex-grow: 1;

  @media (min-width: 1000px) {
    margin-bottom: 10px;
  }

  > input,
  > select,
  > textarea {
    line-height: 1.2;
    box-sizing: border-box;
    border: solid 1px #ccc;
    height: 3rem;
    width: 100%;
    font-size: 16px;
    outline: 0;

    :disabled {
      background: #e6e6e6;
      cursor: not-allowed;
    }

    :not(:disabled) {
      background: white;

      &:focus,
      &:hover {
        border-color: ${props => props.theme.colors.primaryAccent};
      }
    }

    &.-error {
      border-color: #be1e2d;
    }
  }

  > input {
    padding: 6px 10px;
  }

  &.has-icon > input {
    padding: 6px 32px 6px 10px;
  }

  > textarea {
    transition: 0.3s;
    border-bottom-right-radius: 0px;
    padding: 16px 10px 8px 12px;
    min-height: 57px;
    resize: vertical;
  }

  > input[type='number']::-webkit-outer-spin-button,
  > input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  > input[type='number'] {
    -moz-appearance: textfield;
  }

  > select {
    cursor: pointer;
    padding: 7px 20px 5px 10px;

    /* hide default select arrow */
    appearance: none;
    ::-ms-expand {
      display: none;
    }
  }

  > div > label {
    font-weight: 700;
  }

  > ${StyledTooltipIcon} {
    font-size: 1rem;
    position: absolute;
    top: 40%;
    right: 11px;
    cursor: help;
    color: ${props => props.theme.colors.primaryAccent};
  }

  > i.select-indicator {
    font-size: 60%;
    pointer-events: none;
    position: absolute;
    top: 45%;
    right: 14px;
  }

  &.-required {
    > div > label:after {
      content: '*';
      color: red;
      margin-left: 0.5ex;
    }
  }
`;

InputContainerGogurt.defaultProps = {
  theme: defaultTheme as any,
};

export const InputContainer: FC<HTMLAttributes<HTMLDivElement>> = props => {
  const theme = useTheme();
  const { children, ...rest } = props;
  return theme?.name === 'gogurt' ? (
    <InputContainerGogurt {...rest}>{children}</InputContainerGogurt>
  ) : (
    <InputContainerOriginal {...rest}>{children}</InputContainerOriginal>
  );
};
